import * as sentry from '@sentry/react';

import {config} from 'config/env';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {
  ApplyPromotionVoucherResponse,
  Basket,
  BasketItemInput,
  BasketLineItem,
  BasketRoot,
  BasketState,
  BasketUpdateItemQuantityPayload,
  MetaDataPayload,
  MetaDataResponse,
  MultiStore,
  RemovePromotionVoucherResponse,
  TBasketPromotionsLiteResponse,
  TCalculateBasketPayload,
  TGetBasketPromtionsLitePayload,
  UpdateBasketStoreCodeRESTResponse,
  UpdateBasketStoreRESTPayload,
  UserAddress,
  Variant
} from 'types';
import {getProductIdWithoutPrefix} from 'utils';
import {get, post} from 'utils/api';
import {findBasketItemByVariantId, mapBasketData, parseVariantId} from 'utils/basket';
import {cloneObject} from 'utils/cloneObject';

const defaultBasket = {
  basket: {
    // BE fields
    id: '',
    deviceId: '',
    freeShippingValue: 0,
    minimumSpend: 0,
    shippingFee: undefined,
    shippingRule: null,
    shippingFeeRules: [],
    voucherId: '',
    loyaltyPoints: 0,
    loyaltyResponseDetails: undefined,
    estimatedPointsReceive: 0,
    prices: undefined,
    advancedPromotions: undefined,
    availableLoyaltyPoints: 0,
    estimatedPointsDetails: [],
    error: {
      hasError: true,
      message: '',
      code: ''
    },
    unavailableItems: [],

    // FE only fields
    basketItems: [],
    total: 0,
    subtotal: 0,
    discount: 0,
    priceUnit: ''
  } as BasketState
};
async function validateBasket(basket: Basket, storeCodes: string[]): Promise<BasketRoot> {
  try {
    if (basket) {
      const {voucherId, basketItems} = basket;
      const mappedBasket = mapBasketData(basket, storeCodes);
      if (voucherId && basketItems?.length) {
        //TODO: Verify voucher only if basket isn't empty
      }
      return mappedBasket;
    }
    throw new Error('Basket validation error!');
  } catch (e: any) {
    console.error('Error during basket validation:', e);
    return defaultBasket;
  }
}

export async function getBasketCalculation({
  makroMemberCard,
  zipCode,
  subDistrict,
  lang,
  idToken,
  storeCodes = [],
  isExpress,
  basketItems,
  customerId,
  basketMode
}: TCalculateBasketPayload): Promise<BasketRoot> {
  try {
    const payload = {
      makroMemberCard,
      zipCode,
      subDistrict,
      lang,
      isExpress,
      basketItems,
      mode: basketMode
    };

    if (!idToken) throw new Error('IdToken is missing in POST /basket/api/v5/calculate');
    const endPointURl = `${config.BASKET_API_BASE_URL}/basket/api/v5/calculate`;
    const basketResponse: any = await post(endPointURl, payload, true, false, true, true, idToken, true);
    const basketResponseCopy = cloneObject(basketResponse);
    const resp = await validateBasket(
      basketResponse && Object.keys(basketResponse).length > 0 ? basketResponse : defaultBasket.basket,
      storeCodes
    );

    if (Object.keys(basketResponseCopy).length > 0) {
      try {
        const payload = {
          ...basketResponseCopy,
          basketItems: basketResponseCopy?.basketItems?.filter((basketItem: any) => {
            return basketResponseCopy?.basketItemsFromFe?.includes(basketItem?.id);
          }),
          originalBasketItems: basketResponseCopy?.basketItems
        };
        SendBrowserEvent.basketRefreshed(payload);
      } catch (error: any) {
        sentry.captureException(error);
      }
    }
    return resp;
  } catch (error: any) {
    console.error(error);
    const extra = {makroMemberCard, zipCode, subDistrict, lang, isExpress, basketItems, customerId};
    sentry.captureException(error, {extra});
    return defaultBasket;
  }
}

export const updateBasketItemPreview = async (
  basketItems: BasketItemInput[],
  idToken: string,
  isExpress?: boolean
): Promise<any> => {
  try {
    const payload = {
      basketItems,
      isExpress
    };

    const endPointUrl = `${config.BASKET_API_BASE_URL}/basket/api/v5/preview`;
    const {data, error} = await post(endPointUrl, payload, true, false, true, true, idToken, true);
    if (data) {
      return {data, error};
    }
  } catch (error: any) {
    throw new Error(error);
  }
};

export const getBasketPromotionsLite = async (
  basketData: TGetBasketPromtionsLitePayload,
  idToken: string
): Promise<TBasketPromotionsLiteResponse | undefined> => {
  try {
    const endPointURL = new URL(config.CAMPAIGN_SERVICE_URL)?.origin?.concat(
      '/campaign/api/v1/promotions/promotion-lite'
    );
    const data = await post(endPointURL, basketData, true, false, true, true, idToken);
    return data;
  } catch (error: any) {
    console.error('Error getBasketPromotionsLite:', error?.message);

    sentry.captureException(error);

    return undefined;
  }
};

export const changeBasketQuantity = (
  basket: BasketState,
  {quantity, variantId}: Omit<BasketUpdateItemQuantityPayload, 'productId'>
): BasketLineItem[] => {
  const decodedVariantId = parseVariantId(variantId);
  try {
    const matchedItem = findBasketItemByVariantId(basket.basketItems, variantId);
    if (matchedItem) {
      // Validate quantity exceed
      if (quantity && quantity > matchedItem.productDetails.totalInventory) {
        throw new Error('Quantity Exceed');
      } else {
        const {basketItems = [], unavailableItems = []} = basket;
        const updatedItems = basketItems.concat(unavailableItems).map((item: BasketLineItem) => {
          return {
            ...item,
            productId: getProductIdWithoutPrefix(item.productDetails.id),
            quantity: item.variantId === decodedVariantId ? quantity : item.quantity
          };
        });
        const updatedItemsWithPrice = updatedItems
          .map((item: BasketLineItem) => {
            const product = item.productDetails;
            const variant = (product.variants?.length && product.variants[0]) as Variant | undefined;
            return item.variantId === decodedVariantId
              ? {
                  ...item,
                  quantity,
                  price: (variant?.price || 0) * quantity,
                  originPrice: (product?.originPrice || 0) * quantity
                }
              : item;
          })
          .filter((v) => v.quantity !== 0);

        return updatedItemsWithPrice;
      }
    }
    return basket?.basketItems || [];
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const updateBasketStoreCodeREST = async (
  stores: MultiStore[],
  zipCode: string,
  subDistrict: string,
  idToken: string,
  isExpress?: boolean
): Promise<UpdateBasketStoreCodeRESTResponse | undefined> => {
  try {
    const payload: UpdateBasketStoreRESTPayload = {
      zipcode: String(zipCode),
      subdistrict: String(subDistrict),
      stores
    };

    if (ReactNative.isReactNativeWebView()) {
      payload.isExpress = isExpress;
    }
    const endPointURL = new URL(config.BASKET_API_ENDPOINT).origin.concat('/basket/api/v5/updateStoreInfo');
    const response = await post(endPointURL, payload, true, false, true, true, idToken, true);
    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const updateMetaDataV5 = async (
  zipcode: string,
  subdistrict: string,
  idToken: string,
  stores: MultiStore[],
  addressId: string,
  metadata:
    | {
        address: UserAddress;
        subdistrictId: string;
        subdistrictEn: string;
        subdistrictTh: string;
        zipcode: string;
      }
    | any,
  isExpress?: boolean
): Promise<MetaDataResponse | undefined> => {
  try {
    const payload: MetaDataPayload = {
      zipcode,
      subdistrict,
      stores,
      addressId,
      metadata
    };

    if (ReactNative.isReactNativeWebView()) {
      payload.isExpress = isExpress;
    }
    const endPointURL = new URL(config.BASKET_API_ENDPOINT).origin.concat('/basket/api/v5/metadata');
    const response = await post(endPointURL, payload, true, false, true, true, idToken, true);
    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const getMetaDataV5 = async (idToken: string, isExpress?: boolean): Promise<MetaDataResponse | undefined> => {
  try {
    const params = ReactNative.isReactNativeWebView() ? {isExpress: !!isExpress} : {};
    const endPointURL = new URL(config.BASKET_API_ENDPOINT).origin.concat('/basket/api/v5/metadata');
    const metaData = await get(endPointURL, true, true, false, idToken, true, params);
    return metaData;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const removePromotionVoucher = async (
  idToken: string,
  isExpress?: boolean
): Promise<RemovePromotionVoucherResponse | undefined> => {
  try {
    const endPointURL = new URL(config.BASKET_API_ENDPOINT).origin.concat('/basket/api/v5/removePromotionVoucher');
    const payload = {isExpress};
    const response = await post(endPointURL, payload, true, false, true, true, idToken, true);
    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};

export const applyPromotionVoucher = async (
  idToken: string,
  voucherId: string,
  isExpress?: boolean
): Promise<ApplyPromotionVoucherResponse> => {
  try {
    const endPointURL = new URL(config.BASKET_API_ENDPOINT).origin.concat('/basket/api/v5/applyPromotionVoucher');
    const payload = {isExpress, voucherId};
    const response = await post(endPointURL, payload, true, false, true, true, idToken, true);
    return response;
  } catch (error: any) {
    throw new Error(error.message);
  }
};
